import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Phone, Mail, MapPin, Save, Copy, Archive, ArchiveRestore, X,
  User, Home as HomeIcon, KeyRound, StickyNote, DollarSign, Calendar, Clock, Users2, Tag, Repeat,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { STATUS_META, STATUS_ORDER, formatDateTime } from "@/lib/adminMeta";
import { telLink } from "@/data/business";

const inputCls =
  "w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brandgreen focus:border-transparent";

function InfoRow({ label, value }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="flex justify-between gap-3 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-navy text-right">{Array.isArray(value) ? value.join(", ") : value}</span>
    </div>
  );
}

export function LeadDialog({ leadId, open, onClose, onUpdated, teams = [] }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [edit, setEdit] = useState({ status: "new", quote_amount: "", booking_date: "", booking_time: "", cleaning_team: "", recurrence: "none", internal_notes: "", tags: "" });

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    setPhotos([]);
    try {
      const res = await api.get(`/admin/leads/${leadId}`);
      const l = res.data;
      setLead(l);
      setEdit({
        status: l.status || "new",
        quote_amount: l.quote_amount ?? "",
        booking_date: l.booking_date || "",
        booking_time: l.booking_time || "",
        cleaning_team: l.cleaning_team || "",
        recurrence: l.recurrence || "none",
        internal_notes: l.internal_notes || "",
        tags: (l.tags || []).join(", "),
      });
      // load photos as blobs (private)
      (l.photos || []).forEach(async (p) => {
        try {
          const r = await api.get(`/files/${p.file_id}/download`, { responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          setPhotos((prev) => [...prev, { id: p.file_id, url, name: p.filename }]);
        } catch { /* skip */ }
      });
    } catch {
      toast.error("Could not load lead.");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (open) load();
    return () => setPhotos((prev) => { prev.forEach((p) => URL.revokeObjectURL(p.url)); return []; });
  }, [open, load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: edit.status,
        internal_notes: edit.internal_notes,
        cleaning_team: edit.cleaning_team,
        recurrence: edit.recurrence,
        booking_date: edit.booking_date,
        booking_time: edit.booking_time,
        quote_amount: edit.quote_amount === "" ? null : parseFloat(edit.quote_amount),
        tags: edit.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const res = await api.patch(`/admin/leads/${leadId}`, payload);
      toast.success("Lead updated");
      if (res.data?.booking_conflict) {
        (toast.warning || toast)("Possible double-booking: another job is booked at this date/time.");
      }
      onUpdated?.();
    } catch {
      toast.error("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    try {
      await api.post(`/admin/leads/${leadId}/duplicate`);
      toast.success("Lead duplicated");
      onUpdated?.();
      onClose();
    } catch {
      toast.error("Could not duplicate lead.");
    }
  };

  const toggleArchive = async () => {
    try {
      await api.patch(`/admin/leads/${leadId}`, { is_archived: !lead.is_archived });
      toast.success(lead.is_archived ? "Lead restored" : "Lead archived");
      onUpdated?.();
      onClose();
    } catch {
      toast.error("Could not update lead.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="lead-dialog">
        <DialogTitle className="sr-only">Lead details</DialogTitle>
        <DialogDescription className="sr-only">Customer quote request details and lead management.</DialogDescription>
        {loading || !lead ? (
          <div className="h-64 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-navy" /></div>
        ) : (
          <>
            <DialogHeader className="p-6 pb-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-heading text-xl text-navy">{lead.full_name}</h3>
                  <p className="text-sm text-gray-500 mt-1">Received {formatDateTime(lead.created_at)}</p>
                </div>
                <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_META[lead.status]?.color}`}>
                  {STATUS_META[lead.status]?.label}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <a href={telLink} className="inline-flex items-center gap-1.5 rounded-full bg-neutralbg px-3 py-1.5 text-sm font-medium text-navy hover:bg-gray-100"><Phone className="w-3.5 h-3.5" /> {lead.phone}</a>
                <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 rounded-full bg-neutralbg px-3 py-1.5 text-sm font-medium text-navy hover:bg-gray-100"><Mail className="w-3.5 h-3.5" /> {lead.email}</a>
                {lead.suburb && <span className="inline-flex items-center gap-1.5 rounded-full bg-neutralbg px-3 py-1.5 text-sm font-medium text-navy"><MapPin className="w-3.5 h-3.5" /> {lead.suburb}</span>}
              </div>
            </DialogHeader>

            <div className="p-6 grid lg:grid-cols-2 gap-6">
              {/* Left: lead info */}
              <div className="space-y-5">
                <section>
                  <h4 className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2"><HomeIcon className="w-4 h-4" /> Property</h4>
                  <div className="rounded-xl bg-neutralbg/60 px-4 py-2">
                    <InfoRow label="Type" value={lead.property_type} />
                    <InfoRow label="Bedrooms" value={lead.bedrooms} />
                    <InfoRow label="Bathrooms" value={lead.bathrooms} />
                    <InfoRow label="Toilets" value={lead.toilets} />
                    <InfoRow label="Living areas" value={lead.living_areas} />
                    <InfoRow label="Storeys" value={lead.storeys} />
                    <InfoRow label="Garage" value={lead.garage} />
                    <InfoRow label="Condition" value={lead.condition} />
                  </div>
                </section>
                <section>
                  <h4 className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2"><User className="w-4 h-4" /> Services</h4>
                  <div className="rounded-xl bg-neutralbg/60 px-4 py-2"><InfoRow label="Requested" value={lead.services} /><InfoRow label="Contact via" value={lead.preferred_contact} /></div>
                </section>
                <section>
                  <h4 className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2"><KeyRound className="w-4 h-4" /> Access</h4>
                  <div className="rounded-xl bg-neutralbg/60 px-4 py-2">
                    <InfoRow label="Customer home" value={lead.customer_home} />
                    <InfoRow label="Key available" value={lead.key_available} />
                    <InfoRow label="Lockbox" value={lead.lockbox} />
                    <InfoRow label="Apartment access" value={lead.apartment_access} />
                    <InfoRow label="Parking" value={lead.parking_available} />
                    <InfoRow label="Lift" value={lead.lift_available} />
                    <InfoRow label="Pets" value={lead.pets} />
                  </div>
                </section>
                {lead.notes && (
                  <section>
                    <h4 className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2"><StickyNote className="w-4 h-4" /> Customer Notes</h4>
                    <p className="rounded-xl bg-neutralbg/60 px-4 py-3 text-sm text-gray-700">{lead.notes}</p>
                  </section>
                )}
                {(lead.photos || []).length > 0 && (
                  <section>
                    <h4 className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2">Photos ({lead.photos.length})</h4>
                    {photos.length === 0 ? (
                      <p className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading photos…</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {photos.map((p) => (
                          <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="rounded-lg overflow-hidden aspect-square border border-gray-100">
                            <img src={p.url} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                          </a>
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </div>

              {/* Right: management */}
              <div className="space-y-4">
                <div className="rounded-xl border border-gray-100 p-4 space-y-4">
                  <h4 className="font-heading font-semibold text-navy">Manage lead</h4>
                  <div>
                    <label className="text-sm font-medium text-navy mb-1.5 block">Status</label>
                    <Select value={edit.status} onValueChange={(v) => setEdit((e) => ({ ...e, status: v }))}>
                      <SelectTrigger className="h-11" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_ORDER.map((s) => (<SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-navy mb-1.5 flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-brandgreen" /> Quote Amount</label>
                    <input type="number" className={inputCls} value={edit.quote_amount} onChange={(e) => setEdit((s) => ({ ...s, quote_amount: e.target.value }))} placeholder="0.00" data-testid="lead-quote-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-navy mb-1.5 flex items-center gap-1.5"><Calendar className="w-4 h-4 text-brandgreen" /> Booking date</label>
                      <input type="date" className={inputCls} value={edit.booking_date} onChange={(e) => setEdit((s) => ({ ...s, booking_date: e.target.value }))} data-testid="lead-booking-input" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-navy mb-1.5 flex items-center gap-1.5"><Clock className="w-4 h-4 text-brandgreen" /> Booking time</label>
                      <input type="time" className={inputCls} value={edit.booking_time} onChange={(e) => setEdit((s) => ({ ...s, booking_time: e.target.value }))} data-testid="lead-booking-time-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-navy mb-1.5 flex items-center gap-1.5"><Users2 className="w-4 h-4 text-brandgreen" /> Cleaning Team</label>
                      <Select value={edit.cleaning_team || "__none__"} onValueChange={(v) => setEdit((s) => ({ ...s, cleaning_team: v === "__none__" ? "" : v }))}>
                        <SelectTrigger className="h-11" data-testid="lead-team-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Unassigned</SelectItem>
                          {teams.map((t) => (
                            <SelectItem key={t.id} value={t.name}>
                              <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} /> {t.name}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-navy mb-1.5 flex items-center gap-1.5"><Repeat className="w-4 h-4 text-brandgreen" /> Repeats</label>
                      <Select value={edit.recurrence || "none"} onValueChange={(v) => setEdit((s) => ({ ...s, recurrence: v }))}>
                        <SelectTrigger className="h-11" data-testid="lead-recurrence-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">One-off</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="fortnightly">Fortnightly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-navy mb-1.5 flex items-center gap-1.5"><Tag className="w-4 h-4 text-brandgreen" /> Tags</label>
                    <input className={inputCls} value={edit.tags} onChange={(e) => setEdit((s) => ({ ...s, tags: e.target.value }))} placeholder="comma, separated" data-testid="lead-tags-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-navy mb-1.5 block">Internal Notes</label>
                    <textarea rows={4} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brandgreen resize-none" value={edit.internal_notes} onChange={(e) => setEdit((s) => ({ ...s, internal_notes: e.target.value }))} placeholder="Private notes for your team…" data-testid="lead-notes-input" />
                  </div>
                  <button onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brandgreen px-6 py-3 font-semibold text-white hover:bg-brandgreen-dark transition-colors active:scale-95" data-testid="lead-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
                  </button>
                </div>

                <div className="flex gap-3">
                  <button onClick={duplicate} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 px-4 py-2.5 text-sm font-semibold text-navy hover:bg-neutralbg transition-colors" data-testid="lead-duplicate-btn">
                    <Copy className="w-4 h-4" /> Duplicate
                  </button>
                  <button onClick={toggleArchive} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 px-4 py-2.5 text-sm font-semibold text-navy hover:bg-neutralbg transition-colors" data-testid="lead-archive-btn">
                    {lead.is_archived ? <><ArchiveRestore className="w-4 h-4" /> Restore</> : <><Archive className="w-4 h-4" /> Archive</>}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
